import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import moment from 'moment';
import type { UserMessage } from '@bt-common/schemas/bt/user-message';
import { namespace } from '@common/config/constants';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { selectUserId } from '@common-reducers/UserSelectors';
import { fetchFreeLimit, selectFreeLimitStatus } from '@common-reducers/free-limit-slice';
import { planVerdictFor } from '@common/services/schedule-again-plan';
import { shallowEqual } from 'react-redux';
import { openPaymentSite } from '@common-services/utils';
import { setCurrentDate } from '@common-reducers/CalendarReducer';
import { scheduleAgainThunk } from '@common/thunks/schedule-again-thunk';
import {
    messagesLandingInThePast,
    newScheduleAgainNonce,
    scheduleAgainSlotCount,
    type ScheduleAgainOutcome,
    type ScheduleAgainSkipReason,
} from '@common/services/schedule-again';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { usePortalContainer } from '@/common/hooks/use-portal-container';
import { MessageAssetPreview } from '@common/views/calendar/message-asset-preview';
import { scheduleAgainMedia } from './schedule-again-media';
import styles from './schedule-again-dialog.module.scss';

interface Props {
    /** The sent messages to run again. The dialog is open while this is non-empty. */
    messages: UserMessage[];
    onClose: () => void;
    /** Called once the copies were created, so the list can leave select mode. */
    onDone: () => void;
}

/** `<input type="date">` value ("YYYY-MM-DD", local) → local midnight. Empty/invalid → null. */
function parseDay(value: string): Date | null {
    const parsed = moment(value, 'YYYY-MM-DD', true);
    return parsed.isValid() ? parsed.startOf('day').toDate() : null;
}

const recipientName = (m: UserMessage): string => m.contactList?.[0]?.name || m.contactList?.[0]?.id || '';

export default function ScheduleAgainDialog({ messages, onClose, onDone }: Props) {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch();
    const portalContainer = usePortalContainer();
    // The parent keys this component by the ids it is open for, so a new open starts with no date.
    // A synchronous guard on top of `submitting` state: two clicks in one frame both see
    // `submitting === false`, and the second must not start a second batch.
    const submitGuard = useRef(false);
    // One nonce per OPEN of the dialog (it is re-created per open — see the parent's `key`): a repeated
    // request of this submit dedupes on the backend, a later open never collides with it.
    const [nonce] = useState(newScheduleAgainNonce);
    // Required, and empty on purpose: a default of "same day next month" would let a click-through
    // schedule a batch for a date nobody chose.
    const [dayValue, setDayValue] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const mediaItems = useMemo(() => scheduleAgainMedia(messages), [messages]);
    const day = parseDay(dayValue);
    const todayValue = useMemo(() => moment().format('YYYY-MM-DD'), []);
    const pastIds = useMemo(
        () => (day ? messagesLandingInThePast(messages, day, new Date()) : []),
        [messages, day],
    );

    // The free plan caps how many messages can be scheduled at once. The compose dialog checks that
    // for ONE message; a bulk re-run creates one per RECIPIENT, so ask the gate for that many and refuse
    // the whole batch when it does not fit. The thunk asks the same question (`planVerdictFor`) before
    // creating anything — this is only the early, explained answer.
    const userId = useAppSelector(selectUserId);
    const freeLimitStatus = useAppSelector(selectFreeLimitStatus);
    // Same trigger the composer uses: the policy lives in redux, but may never have been asked for.
    useEffect(() => {
        if (userId && freeLimitStatus === 'idle') dispatch(fetchFreeLimit());
    }, [dispatch, userId, freeLimitStatus]);
    const copies = useMemo(() => scheduleAgainSlotCount(messages), [messages]);
    const verdict = useAppSelector(state => planVerdictFor(state, copies), shallowEqual);
    const overPlanLimit = verdict.refused;
    const awaitingPolicy = verdict.awaiting;
    const canSubmit = day !== null && pastIds.length === 0 && !submitting && !overPlanLimit && !awaitingPolicy;

    const reasonText = (reason: ScheduleAgainSkipReason): string => {
        switch (reason) {
            case 'asset-unreachable':
                return t('scheduleAgain.reason.asset', { defaultValue: 'its media is no longer available' });
            case 'past':
                return t('scheduleAgain.reason.past', { defaultValue: 'that time has already passed' });
            case 'plan-limit':
                return t('scheduleAgain.reason.planLimit', { defaultValue: "your plan's scheduled-message limit was reached" });
            case 'ineligible':
                return t('scheduleAgain.reason.ineligible', { defaultValue: 'it cannot be scheduled again' });
            case 'failed':
                return t('scheduleAgain.reason.failed', { defaultValue: 'it could not be scheduled' });
        }
    };

    const report = (outcomes: ScheduleAgainOutcome[], scheduledDay: Date) => {
        const byId = new Map(messages.map(m => [m._id, m]));
        const scheduledCount = outcomes.filter(o => o.status === 'scheduled').length;
        if (scheduledCount > 0) {
            toast.success(
                t('scheduleAgain.toast.scheduled', {
                    count: scheduledCount,
                    defaultValue: scheduledCount === 1 ? '1 message scheduled again' : '{{count}} messages scheduled again',
                }),
                {
                    action: {
                        label: t('scheduleAgain.toast.view', { defaultValue: 'View' }),
                        onClick: () => dispatch(setCurrentDate(scheduledDay)),
                    },
                },
            );
        }
        // One warning per message that was not scheduled, so the user knows exactly which and why.
        for (const outcome of outcomes) {
            if (outcome.status === 'scheduled') continue;
            const original = byId.get(outcome.id);
            const recipient = original ? recipientName(original) : '';
            const reason = reasonText(outcome.status);
            toast.warning(
                t('scheduleAgain.toast.skipped', {
                    recipient,
                    reason,
                    defaultValue: 'Not scheduled: {{recipient}} — {{reason}}.',
                }),
            );
        }
    };

    const submit = async () => {
        if (!day || !canSubmit || submitGuard.current) return;
        submitGuard.current = true;
        setSubmitting(true);
        try {
            const { outcomes } = await dispatch(scheduleAgainThunk({ messages, day, nonce })).unwrap();
            report(outcomes, day);
            onDone();
        } catch (error) {
            toast.error(t('scheduleAgain.toast.error', { defaultValue: 'Could not schedule these messages again.' }));
        } finally {
            submitGuard.current = false;
            setSubmitting(false);
        }
    };

    return (
        <DialogPrimitive.Root open={messages.length > 0} onOpenChange={open => { if (!open && !submitting) onClose(); }}>
            <DialogPrimitive.Portal container={portalContainer}>
                <DialogPrimitive.Overlay className={styles.overlay} />
                <DialogPrimitive.Content className={styles.content}>
                    <DialogPrimitive.Title className={styles.title}>
                        {t('scheduleAgain.title', { defaultValue: 'Schedule again' })}
                    </DialogPrimitive.Title>
                    <DialogPrimitive.Description className={styles.description}>
                        {t('scheduleAgain.description', {
                            count: messages.length,
                            defaultValue: messages.length === 1
                                ? 'Create a new copy of this message with the same text and media. It keeps its original time of day.'
                                : 'Create new copies of these {{count}} messages with the same text and media. Each keeps its original time of day.',
                        })}
                    </DialogPrimitive.Description>

                    {mediaItems.length > 0 && (
                        <div className={styles.media} data-testid="schedule-again-media">
                            <div className={styles.mediaHeading}>
                                {t('scheduleAgain.mediaHeading', { defaultValue: 'Media that will be sent again' })}
                            </div>
                            <ul className={styles.mediaList}>
                                {mediaItems.map(item => (
                                    <li key={item.id} className={styles.mediaRow}>
                                        <MessageAssetPreview type={item.previewType} url={item.url} size={40} />
                                        <span className={styles.mediaText}>
                                            <span className={styles.mediaKind}>{t(`scheduleAgain.media.${item.kind}`, { defaultValue: item.kind })}</span>
                                            <span className={styles.mediaMeta}>{[item.recipient, item.name].filter(Boolean).join(' · ')}</span>
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <label className={styles.field}>
                        {t('scheduleAgain.dateLabel', { defaultValue: 'New date' })}
                        <input
                            type="date"
                            required
                            min={todayValue}
                            value={dayValue}
                            onChange={e => setDayValue(e.target.value)}
                            aria-invalid={pastIds.length > 0}
                            className={styles.dateInput}
                        />
                    </label>

                    {overPlanLimit && (
                        <div role="alert" className={styles.error}>
                            <p className={styles.errorText}>
                                {(verdict.remaining ?? 0) > 0
                                    ? t('scheduleAgain.limit.some', {
                                        count: verdict.remaining ?? 0,
                                        selected: copies,
                                        defaultValue: 'Your plan has room for {{count}} more scheduled messages, but {{selected}} are selected. Select fewer, or upgrade.',
                                    })
                                    : t('scheduleAgain.limit.full', {
                                        defaultValue: "You've reached your plan's scheduled-message limit. Upgrade to schedule more.",
                                    })}
                            </p>
                            <button
                                type="button"
                                className={styles.upgrade}
                                onClick={() => { void openPaymentSite(localStorage.getItem('bt-token') ?? '', true); }}
                            >
                                {t('scheduleAgain.limit.upgrade', { defaultValue: 'Upgrade' })}
                            </button>
                        </div>
                    )}

                    {pastIds.length > 0 && (
                        <p role="alert" className={styles.error}>
                            {t('scheduleAgain.past', {
                                count: pastIds.length,
                                defaultValue: pastIds.length === 1
                                    ? 'On that date, 1 message would be scheduled in the past. Pick a later date.'
                                    : 'On that date, {{count}} messages would be scheduled in the past. Pick a later date.',
                            })}
                        </p>
                    )}

                    <div className={styles.footer}>
                        <button type="button" className={styles.cancel} onClick={onClose} disabled={submitting}>
                            {t('common.cancel', { defaultValue: 'Cancel' })}
                        </button>
                        <button type="button" className={styles.confirm} onClick={submit} disabled={!canSubmit}>
                            {submitting
                                ? t('scheduleAgain.confirming', { defaultValue: 'Scheduling…' })
                                : t('scheduleAgain.confirm', { defaultValue: 'Schedule' })}
                        </button>
                    </div>
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
}
